import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState, RegisterRequest } from '../lib/auth';
import { authApi } from '../lib/api';
import { removeToken, getUser, setUser, removeUser } from '../lib/auth';
import { mapRegisterResponse, RegisterApiResponse } from '../types/api';

interface AuthContextType extends AuthState {
  login: (data: { token: string; user: User; rememberMe?: boolean }) => void;
  register: (data: RegisterRequest) => Promise<{ autoLoggedIn: boolean }>;
  logout: () => Promise<void>;
  setupPassword: (email: string, password: string, confirmPassword: string, token: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_TOKEN'; payload: string | null }
  | { type: 'LOGOUT' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: !!action.payload };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'LOGOUT':
      return { user: null, token: null, isAuthenticated: false, isLoading: false };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from the stored (non-secret) user profile.
  // The access token now lives in an httpOnly cookie, so it is not read here;
  // an invalid/expired cookie surfaces as a 401 on the first API call.
  useEffect(() => {
    const initializeAuth = () => {
      const user = getUser();

      if (user) {
        dispatch({ type: 'SET_USER', payload: user });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    initializeAuth();
  }, []);

  const login = (data: { token: string; user: User; rememberMe?: boolean }) => {
    const { token, user, rememberMe = false } = data;

    // The access token is NOT persisted in web storage (XSS hardening); the
    // backend sets it as an httpOnly cookie. Only the non-secret user profile
    // is stored so the UI can hydrate on reload. The token stays in memory only.
    setUser(user, rememberMe);

    dispatch({ type: 'SET_TOKEN', payload: token });
    dispatch({ type: 'SET_USER', payload: user });
  };

  // register/setupPassword/forgotPassword deliberately do NOT touch the
  // global isLoading flag: ProtectedRoute/PublicRoute (App.tsx) unmount
  // their children while isLoading is true, which was tearing down the
  // calling page mid-request. The page remounted as a fresh component once
  // the request settled, orphaning that request's useMutation onError/
  // onSuccess callbacks -- so a real backend rejection (e.g. duplicate
  // email on register) was silently swallowed with no UI feedback at all.
  // Each page's own mutation.isPending already drives its loading UI, so
  // isLoading here isn't needed for these flows anyway.
  const register = async (data: RegisterRequest) => {
    const response = (await authApi.register({
      companyName: data.companyName,
      contactName: data.contactName,
      email: data.contactEmail,
      password: data.password,
      phoneNumber: data.phoneNumber,
      termsAccepted: data.termsAccepted,
      kvkkConsentGiven: data.kvkkConsentGiven,
    })) as RegisterApiResponse;

    const mapped = mapRegisterResponse(response);
    if (mapped.token && mapped.userId && mapped.organizationId) {
      login({
        token: mapped.token,
        user: {
          id: mapped.userId,
          name: data.contactName,
          email: data.contactEmail,
          role: 'OrganizationAdmin',
          organizationId: mapped.organizationId,
        },
        rememberMe: true,
      });
      return { autoLoggedIn: true };
    }
    return { autoLoggedIn: false };
  };

  const setupPassword = async (email: string, password: string, confirmPassword: string, token: string) => {
    await authApi.setupPassword({ email, password, confirmPassword, token });
  };

  const forgotPassword = async (email: string) => {
    await authApi.forgotPassword(email);
  };

  const logout = async () => {
    // Must be awaited: the server-side token revocation (which is what
    // actually invalidates the session -- see CsrfProtectionMiddleware's
    // sibling ITokenRevocationStore) needs to complete before the UI
    // clears local state and navigates away. Firing this without waiting
    // let the client-side "logged out" state (and the resulting redirect)
    // win a race against the still-in-flight revocation call, especially
    // under load -- confirmed via a real e2e test reusing the pre-logout
    // cookie, which intermittently still worked post-"logout" until this
    // fix. Network errors are still swallowed: a Resend-style outage on
    // this call shouldn't trap the user in a session they can't leave.
    try {
      await authApi.logout();
    } catch {
      // ignore -- still proceed with clearing local state below.
    }
    removeToken();
    removeUser();
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (updatedUser: User) => {
    const rememberMe = !!localStorage.getItem('revomira_user');
    setUser(updatedUser, rememberMe);
    dispatch({ type: 'SET_USER', payload: updatedUser });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    setupPassword,
    forgotPassword,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};